import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog as Ark } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import {
  Bell,
  Clock,
  Code2,
  Compass,
  CornerDownLeft,
  KeyRound,
  Languages,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Search,
  ShieldCheck,
  Star,
  Sun,
  Trash2,
  User,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { getLocale, setLocale, t, tr } from '@/i18n';
import { api, errorMessage } from '@/lib/api';
import { logout, useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { matchCommand, swapLayout, useCommands, type Command } from '@/lib/commands';
import { useTheme } from '@/lib/theme';
import type { Board } from '@/lib/types';
import { useCurrentWorkspace } from '@/dashboard/useCurrentWorkspace';
import { BoardThumb } from '@/dashboard/BoardThumb';

const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

const icon = (I: LucideIcon) => <I />;

interface PublicBoard {
  id: string;
  name: string;
  thumbnailUrl: string | null;
}

/** Палитра команд: Ctrl/⌘+K в любом месте приложения. */
export function CommandPalette() {
  const open = useCommands((s) => s.open);
  const setOpen = useCommands((s) => s.setOpen);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // По физической клавише — работает и в русской раскладке.
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === 'KeyK') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!useCommands.getState().open);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [setOpen]);

  return (
    <Ark.Root open={open} onOpenChange={(d) => setOpen(d.open)} lazyMount unmountOnExit>
      <Portal>
        <Ark.Backdrop className="fixed inset-0 z-[1000] bg-stone-950/20 backdrop-blur-[2px] data-[state=open]:animate-fade-in dark:bg-black/45" />
        <Ark.Positioner className="fixed inset-0 z-[1001] flex items-start justify-center p-3 pt-[12vh]">
          <Ark.Content data-lumo-modal={open ? 'open' : 'closed'} className="glass w-full max-w-xl overflow-hidden !rounded-[20px] shadow-modal outline-none data-[state=open]:animate-pop-in">
            <Ark.Title className="sr-only">{tr("Палитра команд")}</Ark.Title>
            {open && <PaletteBody onClose={() => setOpen(false)} />}
          </Ark.Content>
        </Ark.Positioner>
      </Portal>
    </Ark.Root>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const me = useMe();
  const loggedIn = !!me.data?.user;
  const { current } = useCurrentWorkspace();
  const { setPref } = useTheme();
  const sources = useCommands((s) => s.sources);
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query.trim());
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  // Доски: без запроса — недавние, с запросом — поиск по названию (в обеих раскладках).
  const recent = useQuery({
    queryKey: ['palette-recent'],
    queryFn: () => api<{ boards: Board[] }>('/api/boards', { query: { view: 'recent', limit: 6 } }),
    enabled: loggedIn && !deferred,
    staleTime: 10_000,
  });
  const found = useQuery({
    queryKey: ['palette-search', deferred],
    queryFn: async () => {
      const [a, b] = await Promise.all([
        api<{ boards: PublicBoard[] }>('/api/v1/boards', { query: { q: deferred, limit: 8 } }),
        swapLayout(deferred) !== deferred ? api<{ boards: PublicBoard[] }>('/api/v1/boards', { query: { q: swapLayout(deferred), limit: 4 } }) : Promise.resolve({ boards: [] }),
      ]);
      const seen = new Set<string>();
      return [...a.boards, ...b.boards].filter((x) => !seen.has(x.id) && seen.add(x.id));
    },
    enabled: loggedIn && deferred.length > 0,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  const go = (to: string) => () => navigate(to);

  const commands = useMemo<Command[]>(() => {
    const out: Command[] = [];
    const boards = deferred ? (found.data ?? []) : (recent.data?.boards ?? []);
    for (const b of boards) {
      out.push({
        id: `board:${b.id}`,
        group: deferred ? tr("Доски") : tr("Недавние доски"),
        title: b.name,
        icon: <BoardThumb id={b.id} src={b.thumbnailUrl} className="size-full rounded-[5px]" />,
        keywords: 'доска board',
        run: go(`/board/${b.id}`),
      });
    }

    if (loggedIn) {
      out.push({
        id: 'new-board',
        group: tr("Действия"),
        title: tr("Создать доску"),
        icon: icon(Plus),
        keywords: 'новая доска new board',
        disabled: !current || current.role === 'guest',
        run: async () => {
          try {
            const { board } = await api<{ board: Board }>('/api/boards', { body: { name: t('board.untitled'), workspaceId: current!.id } });
            void qc.invalidateQueries({ queryKey: ['boards'] });
            navigate(`/board/${board.id}`);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        },
      });
    }
    // Команды текущей страницы (доска) — после досок и действий.
    for (const list of Object.values(sources)) out.push(...list);
    if (loggedIn) {
      const nav: Array<[string, string, LucideIcon, string, string?]> = [
        ['nav-all', tr("Все доски"), LayoutDashboard, '/dashboard', tr("дашборд главная")],
        ['nav-recent', tr("Недавние"), Clock, '/dashboard/recent'],
        ['nav-fav', tr("Избранное"), Star, '/dashboard/favorites'],
        ['nav-tpl', tr("Шаблоны"), LayoutTemplate, '/dashboard/templates', 'templates'],
        ['nav-trash', tr("Корзина"), Trash2, '/dashboard/trash'],
        ['nav-notif', tr("Уведомления"), Bell, '/notifications'],
        ['nav-explore', tr("Галерея публичных досок"), Compass, '/explore', 'explore'],
        ['nav-profile', tr("Профиль"), User, '/settings/profile', tr("настройки settings")],
        ['nav-tokens', tr("API-токены"), KeyRound, '/settings/tokens', tr("настройки api token")],
        ...(current ? ([['nav-hooks', tr("Вебхуки"), Webhook, `/settings/webhooks/${current.id}`, tr("настройки webhook")]] as Array<[string, string, LucideIcon, string, string?]>) : []),
        ['nav-dev', tr("Документация API"), Code2, '/developers', tr("разработчикам openapi")],
        ...(me.data?.user?.isAdmin
          ? ([
              ['nav-admin', tr("Администрирование: обзор"), ShieldCheck, '/settings/admin', tr("админка admin инстанс")],
              ['nav-admin-users', tr("Администрирование: пользователи"), ShieldCheck, '/settings/admin/users', tr("админка admin блокировка")],
              ['nav-admin-boards', tr("Администрирование: доски"), ShieldCheck, '/settings/admin/boards', tr("админка admin модерация")],
              ['nav-admin-instance', tr("Администрирование: настройки инстанса"), ShieldCheck, '/settings/admin/instance', tr("админка admin регистрация объявление")],
            ] as Array<[string, string, LucideIcon, string, string?]>)
          : []),
      ];
      for (const [id, title, I, to, kw] of nav) {
        if (location.pathname === to) continue;
        out.push({ id, group: tr("Перейти"), title, icon: icon(I), keywords: kw, run: go(to) });
      }
    } else {
      out.push({ id: 'login', group: tr("Перейти"), title: tr("Войти"), icon: icon(User), keywords: 'login вход', run: go('/login') });
      out.push({ id: 'explore', group: tr("Перейти"), title: tr("Галерея публичных досок"), icon: icon(Compass), run: go('/explore') });
    }

    out.push(
      { id: 'theme-light', group: tr("Тема"), title: tr("Светлая тема"), icon: icon(Sun), keywords: 'theme light', run: () => setPref('light') },
      { id: 'theme-dark', group: tr("Тема"), title: tr("Тёмная тема"), icon: icon(Moon), keywords: 'theme dark ночь', run: () => setPref('dark') },
      { id: 'lang-ru', group: tr("Язык"), title: 'Русский', icon: icon(Languages), keywords: 'language язык russian', disabled: getLocale() === 'ru', run: () => setLocale('ru') },
      { id: 'lang-en', group: tr("Язык"), title: 'English', icon: icon(Languages), keywords: 'language язык english английский', disabled: getLocale() === 'en', run: () => setLocale('en') },
      { id: 'theme-system', group: tr("Тема"), title: tr("Тема как в системе"), icon: icon(Monitor), keywords: 'theme system', run: () => setPref('system') },
    );
    if (loggedIn) out.push({ id: 'logout', group: tr("Аккаунт"), title: tr("Выйти"), icon: icon(LogOut), keywords: 'logout выход', run: () => void logout() });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, found.data, recent.data, deferred, loggedIn, current, location.pathname]);

  const results = useMemo(() => {
    const q = query.trim();
    const scored = commands
      .map((c, i) => ({ c, i, s: c.id.startsWith('board:') ? 1 : matchCommand(c, q) }))
      .filter((x) => x.s > 0);
    // Без запроса — исходный порядок групп; с запросом — по релевантности (группы сохраняются).
    if (q) scored.sort((a, b) => b.s - a.s || a.i - b.i);
    const groups = new Map<string, Command[]>();
    for (const { c } of scored) {
      if (!groups.has(c.group)) groups.set(c.group, []);
      const g = groups.get(c.group)!;
      if (!q || g.length < 8) g.push(c);
    }
    return [...groups.entries()];
  }, [commands, query]);

  const flat = useMemo(() => results.flatMap(([, items]) => items), [results]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const run = async (c: Command | undefined) => {
    if (!c || c.disabled) return;
    onClose();
    // Даём диалогу закрыться — команда может открыть свой диалог или сфокусировать холст.
    await new Promise((r) => setTimeout(r, 0));
    await c.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(flat.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void run(flat[active]);
    }
  };

  let index = -1;
  const loading = !!deferred && found.isFetching;
  return (
    <div onKeyDown={onKeyDown}>
      <div className="flex items-center gap-3 border-b border-border/70 px-4">
        <Search className="size-[18px] shrink-0 text-fg-subtle" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={loggedIn ? tr("Найти доску или команду…") : tr("Найти команду…")}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={flat[active] ? `${listId}-${active}` : undefined}
          aria-autocomplete="list"
          className="h-14 min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-fg-subtle"
        />
        {loading && <span className="size-4 animate-spin rounded-full border-2 border-fg-subtle/30 border-t-fg-subtle" aria-hidden />}
        <kbd className="rounded-[6px] bg-surface-2 px-1.5 py-0.5 font-sans text-[11px] text-fg-subtle">Esc</kbd>
      </div>
      <div ref={listRef} id={listId} role="listbox" aria-label={tr("Результаты")} className="max-h-[min(420px,60vh)] overflow-y-auto overscroll-contain p-2">
        {flat.length === 0 && (
          <div className="px-3 py-10 text-center text-sm text-fg-muted">
            {loading ? tr("Ищем…") : <>{tr("Ничего не нашлось по запросу «")}{query.trim()}»</>}
          </div>
        )}
        {results.map(([group, items]) => (
          <div key={group} role="group" aria-label={group} className="mb-1 last:mb-0">
            <div className="px-2.5 pt-2 pb-1 text-[11px] font-medium tracking-wide text-fg-subtle uppercase">{group}</div>
            {items.map((c) => {
              index++;
              const i = index;
              const selected = i === active;
              return (
                <div
                  key={c.id}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={c.disabled || undefined}
                  onMouseMove={() => active !== i && setActive(i)}
                  onClick={() => void run(c)}
                  className={cn(
                    'flex h-10 cursor-pointer items-center gap-3 rounded-[10px] px-2.5 text-[14px] select-none',
                    selected ? 'bg-accent-soft text-accent-fg' : 'text-fg',
                    c.disabled && 'cursor-not-allowed opacity-40',
                  )}
                >
                  <span className={cn('flex size-6 shrink-0 items-center justify-center overflow-hidden [&>svg]:size-[17px] [&>svg]:stroke-[1.75]', selected ? 'text-accent-fg' : 'text-fg-muted')}>{c.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{c.title}</span>
                  {c.hint && <span className="truncate text-[12px] text-fg-subtle">{c.hint}</span>}
                  {c.shortcut && <kbd className="rounded-[6px] bg-surface-2 px-1.5 py-0.5 font-sans text-[11px] text-fg-subtle">{c.shortcut}</kbd>}
                  {selected && !c.shortcut && <CornerDownLeft className="size-3.5 text-accent-fg/70" aria-hidden />}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 border-t border-border/70 px-4 py-2.5 text-[11.5px] text-fg-subtle">
        <span>
          <kbd className="font-sans">↑↓</kbd> {tr("выбрать")}
        </span>
        <span>
          <kbd className="font-sans">↵</kbd> {tr("выполнить")}
        </span>
        <span className="ml-auto">
          <kbd className="font-sans">{MOD}K</kbd> {tr("открыть / закрыть")}
        </span>
      </div>
    </div>
  );
}
